// My Applications — pure helpers: status → pill mapping, date/time formatting,
// title cleanup. No React here; keeps the page component focused on markup.

const norm = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Backend import-job titles sometimes carry an internal `[IMPORTED]` marker —
// strip it so candidates never see data-pipeline plumbing.
export function cleanJobTitle(title) {
  return String(title || '').replace(/^\s*\[imported\]\s*/i, '');
}

export function humanize(raw) {
  if (!raw) return '';
  return norm(raw).replace(/\b\w/g, (c) => c.toUpperCase());
}

// Interest-request status → pill variant + label. `acknowledged_at` (the
// candidate's own action) takes priority over whatever free-text `status`
// the backend happens to send, since it's the one fact we're certain of.
const INTEREST_STATUS = {
  'not interested': { variant: 'muted', label: 'Not interested' },
  rejected: { variant: 'destructive', label: 'Not selected' },
  'removed from pipeline': { variant: 'muted', label: 'Closed' },
};

export function interestStatusPill(item) {
  const key = norm(item?.status);
  if (INTEREST_STATUS[key]) return INTEREST_STATUS[key];
  if (item?.acknowledged_at) return { variant: 'success', label: 'Interested' };
  return {
    variant: 'info',
    label: item?.status ? humanize(item.status) : 'Contacted',
  };
}

// True once the candidate still owes a yes/no on the outreach itself.
export function interestNeedsResponse(item) {
  const key = norm(item?.status);
  return !item?.acknowledged_at && !INTEREST_STATUS[key];
}

// Interview status → pill variant + label.
export function interviewStatusPill(item) {
  if (item?.candidate_rejected)
    return { variant: 'destructive', label: "Can't attend" };
  const key = norm(item?.status);
  if (key === 'cancelled' || key === 'canceled')
    return { variant: 'destructive', label: 'Cancelled' };
  if (key === 'completed' || key === 'done')
    return { variant: 'muted', label: 'Completed' };
  if (item?.requires_acknowledgment && !item?.candidate_acknowledged)
    return { variant: 'warning', label: 'Needs your response' };
  if (item?.candidate_acknowledged)
    return { variant: 'success', label: 'Confirmed' };
  if (item?.is_rescheduled) return { variant: 'warning', label: 'Rescheduled' };
  return { variant: 'info', label: 'Scheduled' };
}

export function interviewNeedsResponse(item) {
  return !!(
    item?.requires_acknowledgment &&
    !item?.candidate_acknowledged &&
    !item?.candidate_rejected
  );
}

export function fmtDateShort(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

// Interview window in the candidate's local time — the API sends UTC.
export function fmtInterviewWhen(startIso, endIso) {
  const start = startIso ? new Date(startIso) : null;
  if (!start || Number.isNaN(start.getTime())) {
    return { date: '—', time: '—' };
  }
  const end = endIso ? new Date(endIso) : null;
  const date = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(start);
  const fmtTime = (d) =>
    new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(d);
  const time =
    end && !Number.isNaN(end.getTime())
      ? `${fmtTime(start)} – ${fmtTime(end)}`
      : fmtTime(start);
  return { date, time };
}
